import { Currency, RevenuePeriod } from '@vendasta/sales-orders';
import { Currency as BillingCurrency, Frequency, ProductPricing } from '@galaxy/billing';
import { CurrenciesCurrency, FrequenciesFrequency, Pricing } from '@vendasta/marketplace-packages';
import { UILineItem } from './interface';

export function getStorePriceForPackage(
  packageId: string,
  currency: Currency,
  frequency: RevenuePeriod,
  storePackagePrices: { [key: string]: Pricing },
): number | null {
  if (!packageId || !storePackagePrices) {
    return null;
  }
  const packagePricing: Pricing = storePackagePrices[packageId];
  if (!packagePricing) {
    return null;
  }
  if (!salesOrderCurrencyEqualToMarketplaceCurrency(currency, packagePricing.currency)) {
    return null;
  }
  const packagePrice = packagePricing.prices?.find((price) =>
    salesOrderPeriodEqualToMarketplaceFrequency(frequency, price.frequency),
  );
  return packagePrice?.price || null;
}

export function getStorePriceForApp(
  appId: string,
  currency: Currency,
  frequency: RevenuePeriod,
  storeAppPrices: { [key: string]: ProductPricing },
): number | null {
  if (!appId || !storeAppPrices) {
    return null;
  }
  const appPricing: ProductPricing = storeAppPrices[appId];
  if (!appPricing) {
    return null;
  }
  if (!salesOrderCurrencyEqualToBillingCurrency(currency, appPricing.currency)) {
    return null;
  }
  if (frequency !== billingFrequencyToRevenuePeriod(appPricing.frequency)) {
    return null;
  }
  return appPricing.pricingRules[0]?.price || null;
}

export function salesOrderPeriodEqualToMarketplaceFrequency(
  period: RevenuePeriod,
  frequency: FrequenciesFrequency,
): boolean {
  const salesOrderPeriod = period || RevenuePeriod.ONETIME;
  const marketplaceFrequency = frequency || FrequenciesFrequency.MONTHLY;
  return salesOrderPeriodToMarketplaceFrequency[salesOrderPeriod] === marketplaceFrequency;
}

export function salesOrderCurrencyEqualToMarketplaceCurrency(
  salesOrderCurrency: Currency,
  marketplaceCurrency: CurrenciesCurrency,
): boolean {
  const soCurrency = salesOrderCurrency || Currency.USD;
  const mpCurrency = marketplaceCurrency || CurrenciesCurrency.USD;
  return salesOrderCurrencyToMarketplaceCurrency[soCurrency] === mpCurrency;
}

export function salesOrderCurrencyEqualToBillingCurrency(
  salesOrderCurrency: Currency,
  billingCurrency: BillingCurrency,
): boolean {
  const soCurrency = salesOrderCurrency || Currency.USD;
  const bCurrency = billingCurrency || BillingCurrency.USD;
  return salesOrderCurrencyToBillingCurrency[soCurrency] === bCurrency;
}

export function billingFrequencyToRevenuePeriod(frequency: Frequency): RevenuePeriod | null {
  switch (frequency) {
    case Frequency.Monthly:
      return RevenuePeriod.MONTHLY;
    case Frequency.Yearly:
      return RevenuePeriod.YEARLY;
    case Frequency.OneTime:
      return RevenuePeriod.ONETIME;
    default:
      return null;
  }
}

export function getCurrencyFromLineItem(lineItem: UILineItem): Currency {
  if (lineItem.lineItem.currency) {
    return lineItem.lineItem.currency;
  }
  return currencyCodeToCurrency[lineItem.lineItem.currencyCode] || Currency.USD;
}

const currencyCodeToCurrency = {
  USD: Currency.USD,
  CAD: Currency.CAD,
  EUR: Currency.EUR,
  AUD: Currency.AUD,
  GBP: Currency.GBP,
  NZD: Currency.NZD,
  ZAR: Currency.ZAR,
};

const salesOrderPeriodToMarketplaceFrequency = {
  [RevenuePeriod.ONETIME]: FrequenciesFrequency.ONCE,
  [RevenuePeriod.DAILY]: FrequenciesFrequency.DAILY,
  [RevenuePeriod.WEEKLY]: FrequenciesFrequency.WEEKLY,
  [RevenuePeriod.BIWEEKLY]: FrequenciesFrequency.OTHER,
  [RevenuePeriod.MONTHLY]: FrequenciesFrequency.MONTHLY,
  [RevenuePeriod.YEARLY]: FrequenciesFrequency.YEARLY,
};

const salesOrderCurrencyToMarketplaceCurrency = {
  [Currency.AUD]: CurrenciesCurrency.AUD,
  [Currency.CAD]: CurrenciesCurrency.CAD,
  [Currency.EUR]: CurrenciesCurrency.EUR,
  [Currency.GBP]: CurrenciesCurrency.GBP,
  [Currency.NZD]: CurrenciesCurrency.NZD,
  [Currency.USD]: CurrenciesCurrency.USD,
  [Currency.ZAR]: CurrenciesCurrency.ZAR,
};

const salesOrderCurrencyToBillingCurrency = {
  [Currency.AUD]: BillingCurrency.AUD,
  [Currency.CAD]: BillingCurrency.CAD,
  [Currency.EUR]: BillingCurrency.EUR,
  [Currency.GBP]: BillingCurrency.GBP,
  [Currency.NZD]: BillingCurrency.NZD,
  [Currency.USD]: BillingCurrency.USD,
  [Currency.ZAR]: BillingCurrency.ZAR,
};
